// 地图瓦片本地缓存：IndexedDB 存储 + LRU 容量上限 + 持久化申请。
// 缓存键 = 瓦片完整 URL（Leaflet 对同一 coords 生成的 URL 是确定性的）；
// 命中后以 object URL 提供给 <img>，避免每次进入都跨洋重新下载。
const DB_NAME = 'map-tile-cache'
const DB_VERSION = 1
const STORE_NAME = 'tiles'
const DEFAULT_MAX_BYTES = 300 * 1024 * 1024 // 默认容量上限 300MB

let db = null
let openPromise = null
let maxBytes = DEFAULT_MAX_BYTES
// url -> objectURL，统一登记回收，避免 SPA 路由切换时泄漏
const blobUrls = new Map()

function open() {
  if (openPromise) return openPromise
  openPromise = new Promise(resolve => {
    if (typeof indexedDB === 'undefined') { resolve(null); return }
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const store = req.result.createObjectStore(STORE_NAME, { keyPath: 'url' })
      store.createIndex('lastUsed', 'lastUsed')
    }
    req.onsuccess = () => { db = req.result; resolve(db) }
    req.onerror = () => { resolve(null) } // 打不开则退化为纯网络加载
    req.onblocked = () => { resolve(null) }
  })
  return openPromise
}

// 申请持久存储，尽量防止缓存被浏览器/系统回收
function requestPersist() {
  if (navigator.storage && navigator.storage.persist) {
    navigator.storage.persist().catch(() => {})
  }
}

function getBlob(url) {
  return open().then(d => {
    if (!d) return null
    return new Promise(resolve => {
      const tx = d.transaction(STORE_NAME, 'readonly')
      const req = tx.objectStore(STORE_NAME).get(url)
      req.onsuccess = () => {
        const rec = req.result
        if (rec && rec.blob) {
          // 触碰 LRU 时间戳（尽力而为，失败不影响返回）
          rec.lastUsed = Date.now()
          const wtx = d.transaction(STORE_NAME, 'readwrite')
          wtx.objectStore(STORE_NAME).put(rec)
          resolve(rec.blob)
        } else {
          resolve(null)
        }
      }
      req.onerror = () => resolve(null)
    })
  })
}

function save(url, blob) {
  return open().then(d => {
    if (!d) return Promise.resolve()
    return new Promise(resolve => {
      const tx = d.transaction(STORE_NAME, 'readwrite')
      tx.objectStore(STORE_NAME).put({ url, blob, lastUsed: Date.now(), size: blob.size })
      tx.oncomplete = () => { enforceLimit(d); resolve() }
      tx.onerror = () => resolve()
    })
  })
}

// 超出容量上限时，按 lastUsed 从小到大（最旧优先）删除直至回到上限内
function enforceLimit(d) {
  if (!d.objectStoreNames.contains(STORE_NAME)) return
  const tx = d.transaction(STORE_NAME, 'readonly')
  const idx = tx.objectStore(STORE_NAME).index('lastUsed')
  const req = idx.openCursor(null, 'next')
  let total = 0
  const entries = []
  req.onsuccess = () => {
    const cur = req.result
    if (cur) {
      total += (cur.value.size || 0)
      entries.push(cur)
      cur.continue()
    } else if (total > maxBytes) {
      const wtx = d.transaction(STORE_NAME, 'readwrite')
      const st = wtx.objectStore(STORE_NAME)
      let needFree = total - maxBytes
      for (let i = 0; i < entries.length && needFree > 0; i++) {
        st.delete(entries[i].key)
        needFree -= (entries[i].value.size || 0)
      }
    }
  }
}

// 网络加载完成后回存：fetch 使用浏览器 HTTP 缓存（<img> 刚加载过，缓存已热），
// 不产生第二次真实网络流量；源站无 CORS 头被拒时静默跳过，不影响显示。
function saveFromNetwork(url) {
  fetch(url, { cache: 'force-cache' })
    .then(resp => (resp.ok ? resp.blob() : null))
    .then(blob => {
      if (blob && blob.size > 0) return save(url, blob)
    })
    .catch(() => {})
}

// 命中缓存时返回已登记的 object URL；未命中返回 null（由调用方走网络）
function getObjectUrl(url) {
  return getBlob(url).then(blob => {
    if (!blob) return null
    let objUrl = blobUrls.get(url)
    if (!objUrl) {
      objUrl = URL.createObjectURL(blob)
      blobUrls.set(url, objUrl)
    }
    return objUrl
  })
}

function revoke(url) {
  const objUrl = blobUrls.get(url)
  if (objUrl) {
    blobUrls.delete(url)
    try { URL.revokeObjectURL(objUrl) } catch (e) { /* 忽略回收失败 */ }
  }
}

function revokeAll() {
  blobUrls.forEach(objUrl => { try { URL.revokeObjectURL(objUrl) } catch (e) { /* 忽略回收失败 */ } })
  blobUrls.clear()
}

export default {
  init() {
    requestPersist()
    return open()
  },
  getObjectUrl,
  saveFromNetwork,
  revoke,
  revokeAll,
  setMaxBytes(v) { maxBytes = v || maxBytes }
}
